// src/hooks/useInventoryPublic.jsx (COMPLETO)
import { useState, useEffect, useCallback, useRef } from "react";
import supabase from "../SupaCredentials";
import { PRODUCT_CONFIG } from "../config/products";

// Cache global para toda la aplicación
let globalInventoryCache = {
  data: null,
  timestamp: null,
  isLoaded: false,
};

// Tiempo de cache en milisegundos (10 minutos)
const CACHE_DURATION = 10 * 60 * 1000;

export const useInventoryPublic = () => {
  const [stockData, setStockData] = useState({
    jugs: 0,
    dispensers: 0,
    isLoading: false,
    isLoaded: false,
    lastUpdated: null,
    error: null,
  });
  
  const [isRefreshing, setIsRefreshing] = useState(false);
  const mountedRef = useRef(true);
  const initialLoadRef = useRef(false);

  // ================================
  // FUNCIÓN DE CARGA CORREGIDA
  // ================================

  const loadStockFromAPI = useCallback(async () => {
    try {
      console.log("🔄 Consultando inventario desde API...");

      // Obtener dispensadores desde inventory_units con status 'available'
      const { data: dispenserProduct, error: productError } = await supabase
        .from("products")
        .select("id")
        .eq("sku_code", PRODUCT_CONFIG.DISPENSERS.SKU)
        .single();

      let dispensersCount = 0;
      if (!productError && dispenserProduct) {
        const { count: dispensersAvailable, error: dispensersError } = await supabase
          .from("inventory_units")
          .select("id", { count: "exact" })
          .eq("product_id", dispenserProduct.id)
          .eq("status", "available");

        if (dispensersError) {
          console.warn("Error obteniendo dispensadores:", dispensersError);
        } else {
          dispensersCount = dispensersAvailable || 0;
        }
      } else {
        console.warn("Error obteniendo producto dispensador:", productError);
      }

      // Obtener botellones desde jugs con operational_state 'in_inventory'
      const { count: jugsCount, error: jugsError } = await supabase
        .from("jugs")
        .select("id", { count: "exact" })
        .eq("operational_state", "in_inventory")
        .eq("active", true);

      if (jugsError) {
        throw jugsError;
      }

      const result = {
        jugs: jugsCount || 0,
        dispensers: dispensersCount,
        timestamp: Date.now(),
      };

      // Actualizar cache global
      globalInventoryCache = {
        data: result,
        timestamp: result.timestamp,
        isLoaded: true,
      };

      console.log("✅ Inventario cargado y cacheado:", result);
      return result;

    } catch (error) {
      console.error("❌ Error cargando inventario:", error);
      throw error;
    }
  }, []);

  // ================================
  // FUNCIÓN DE CARGA CON CACHE
  // ================================

  const loadStock = useCallback(async (forceRefresh = false) => {
    if (!mountedRef.current) return;

    // Verificar si el cache es válido
    const now = Date.now();
    const cacheAge = globalInventoryCache.timestamp ? now - globalInventoryCache.timestamp : Infinity;
    const isCacheValid = cacheAge < CACHE_DURATION && globalInventoryCache.isLoaded;

    // Si tenemos cache válido y no es refresh forzado, usar cache
    if (isCacheValid && !forceRefresh && globalInventoryCache.data) {
      console.log("📦 Usando inventario desde cache");
      
      const newStockData = {
        jugs: globalInventoryCache.data.jugs,
        dispensers: globalInventoryCache.data.dispensers,
        isLoading: false,
        isLoaded: true,
        lastUpdated: new Date(globalInventoryCache.timestamp),
        error: null,
      };

      setStockData(newStockData);
      return newStockData;
    }

    // Necesitamos cargar desde API
    if (forceRefresh) {
      setIsRefreshing(true);
    } else {
      setStockData(prev => ({ ...prev, isLoading: true, error: null }));
    }

    try {
      const result = await loadStockFromAPI();

      if (!mountedRef.current) return;

      const newStockData = {
        jugs: result.jugs,
        dispensers: result.dispensers,
        isLoading: false,
        isLoaded: true,
        lastUpdated: new Date(result.timestamp),
        error: null,
      };

      setStockData(newStockData);
      return newStockData;

    } catch (error) {
      if (mountedRef.current) {
        setStockData(prev => ({
          ...prev,
          isLoading: false,
          error: error.message,
          // Mantener datos anteriores si existían
          isLoaded: prev.isLoaded || globalInventoryCache.isLoaded,
        }));
      }
      return null;
    } finally {
      if (mountedRef.current) {
        setIsRefreshing(false);
      }
    }
  }, [loadStockFromAPI]);

  // ================================
  // FUNCIONES PÚBLICAS
  // ================================

  const refreshStock = useCallback(async () => {
    console.log("🔄 Refresh manual solicitado");
    return await loadStock(true);
  }, [loadStock]);

  const hasStock = useCallback(() => {
    return stockData.jugs > 0 || stockData.dispensers > 0;
  }, [stockData]);

  const shouldShowDispensers = useCallback(() => {
    return stockData.dispensers > 0;
  }, [stockData]);

  const getMaxQuantity = useCallback((type) => {
    if (type === "jugs") return stockData.jugs;
    if (type === "dispensers") return stockData.dispensers;
    return 0;
  }, [stockData]);

  const validateQuantity = useCallback((type, quantity) => {
    const max = getMaxQuantity(type);
    return {
      isValid: quantity <= max,
      max,
      requested: quantity,
    };
  }, [getMaxQuantity]);

  const getCacheInfo = useCallback(() => {
    const now = Date.now();
    const cacheAge = globalInventoryCache.timestamp ? now - globalInventoryCache.timestamp : null;
    
    return {
      hasCachedData: !!globalInventoryCache.data,
      cacheAge: cacheAge ? Math.round(cacheAge / 1000) : null, // en segundos
      isExpired: cacheAge ? cacheAge > CACHE_DURATION : true,
      lastCacheUpdate: globalInventoryCache.timestamp ? new Date(globalInventoryCache.timestamp) : null,
    };
  }, []);

  // ================================
  // EFECTOS OPTIMIZADOS
  // ================================

  // Solo carga inicial, SIN actualizaciones automáticas
  useEffect(() => {
    mountedRef.current = true;
    
    if (!initialLoadRef.current) {
      initialLoadRef.current = true;
      loadStock();
    }

    return () => {
      mountedRef.current = false;
    };
  }, []); // Solo al montar, sin dependencias de loadStock

  // ELIMINAR efectos de actualización automática para mayor fluidez
  // NO hay useEffect para:
  // - Actualizaciones periódicas cada X minutos
  // - Actualizaciones al cambiar de pestaña
  // - Actualizaciones automáticas

  // ================================
  // RETORNO
  // ================================

  return {
    // Estados principales
    ...stockData,
    isRefreshing,

    // Funciones de utilidad
    hasStock: hasStock(),
    shouldShowDispensers: shouldShowDispensers(),
    
    // Funciones de acceso
    getMaxQuantity,
    validateQuantity,
    refreshStock, // Solo refresh manual

    // Información de cache para debugging
    cacheInfo: getCacheInfo(),

    // Para debugging
    debug: {
      stockData,
      isRefreshing,
      globalCache: globalInventoryCache,
      cacheInfo: getCacheInfo(),
    },
  };
};